import { CROP_PHASE_LABELS, MONTH_NAMES_PT } from '../constants/maintenance.constants';
import type { ParcellingTableRow } from '../components/Tables/ParcellingTable/ParcellingTable';
import { findCycleStartIndex, parseMonthNumber } from './parcellingCycle';

type NutrientParcel = {
	month?: number;
	weeks?: number;
	phase?: string;
	N?: number;
	P?: number;
	K?: number;
	Ca?: number;
	Mg?: number;
	S?: number;
	B?: number;
	Cu?: number;
	Fe?: number;
	Mn?: number;
	Zn?: number;
};

export type ParcellingTableSource = {
	typeParcelling?: 'fixed' | 'dynamic';
	fixedParcel?: NutrientParcel[];
	dynamicParcel?: NutrientParcel[];
};

const toRow = (
	month: string,
	parcel: NutrientParcel
): ParcellingTableRow => ({
	month,
	phase: CROP_PHASE_LABELS[parcel.phase ?? ''] ?? parcel.phase ?? '—',
	n: Number(parcel.N ?? 0),
	p: Number(parcel.P ?? 0),
	k: Number(parcel.K ?? 0),
	ca: Number(parcel.Ca ?? 0),
	mg: Number(parcel.Mg ?? 0),
	s: Number(parcel.S ?? 0),
	b: Number(parcel.B ?? 0),
	cu: Number(parcel.Cu ?? 0),
	fe: Number(parcel.Fe ?? 0),
	mn: Number(parcel.Mn ?? 0),
	zn: Number(parcel.Zn ?? 0),
});

const monthLabelFromIndex = (monthIndex: number) => {
	const key = String((monthIndex % 12) + 1).padStart(2, '0');
	return MONTH_NAMES_PT[key] ?? key;
};

export const mapParcellingToTableRows = (
	parcelling?: ParcellingTableSource | null,
	startMonth?: string | number | null
): ParcellingTableRow[] => {
	if (!parcelling) return [];

	if (parcelling.typeParcelling === 'dynamic') {
		return (parcelling.dynamicParcel ?? []).map((parcel, index) =>
			toRow(`${index + 1}ª semana`, parcel)
		);
	}

	const parcels = parcelling.fixedParcel ?? [];
	const startIndex = findCycleStartIndex(parcels, startMonth);
	const startNumber = parseMonthNumber(startMonth);
	const rotated =
		startIndex >= 0 ? [...parcels.slice(startIndex), ...parcels.slice(0, startIndex)] : parcels;
	const origin = startIndex >= 0 && startNumber != null ? startNumber - 1 : startNumber != null ? startNumber - 1 : null;

	return rotated.map((parcel, index) => {
		const monthIndex = origin == null ? (parcel.month ? parcel.month - 1 : index) : origin + index;
		return toRow(monthLabelFromIndex(monthIndex), parcel);
	});
};
